import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, Redirect } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";
import ReadleeAudioPlayer from "../Screens/BookScreen/QueueCards/ReadleeAudioPlayer";
import ItemNav from "../AssignmentReport/ItemNav";
import QuestionCard from "../AssignmentReport/QuestionCard";
import FirebaseService from "../../services/firebase/FirebaseService";
import UploadService from "../../services/upload/UploadService";
import sortTranscripts from "../../utils/SortTranscripts";
import { RootState } from "../../state/configureStore";
import RecordedPage from "../../interfaces/RecordedPage";
import Page from "../../interfaces/Page";

const AssignmentReading = () => {
  const { assignmentId, studentId } = useParams<{ assignmentId: string; studentId?: string }>();
  const bookId = useSelector((state: RootState) => state.teacherState.assignments)[assignmentId]?.assignmentDetails
    ?.bookId;
  const [recordedPages, setRecordedPages] = useState<Array<RecordedPage> | null>(null);
  const [index, setIndex] = useState(0);
  const [pageAudio, setPageAudio] = useState<Array<string> | null>(null);
  const [audioDurations, setAudioDurations] = useState<Array<number> | null>(null);
  const [pages, setPages] = useState<Array<Page>>([]);

  const assignmentClasses = useSelector((state: RootState) => state.teacherState.assignmentClasses);
  const students = Object.values(assignmentClasses || {}).flatMap(ac =>
    Object.values(ac || {}).flatMap(({ students = {} }) => Object.keys(students)),
  );

  const recordedPage = recordedPages?.[index];
  const transcripts = useMemo(
    () => Object.entries(recordedPage?.transcripts || {}).sort(sortTranscripts),
    [recordedPage],
  );
  const section = transcripts[index]?.[0];
  const currentPage = pages[index];

  useEffect(() => {
    const fetchRecordedPages = async () => {
      if (!(studentId && bookId)) return;
      setRecordedPages(await FirebaseService.getRecordedPages(studentId, bookId));
    };
    fetchRecordedPages();
  }, [studentId, bookId]);

  useEffect(() => {
    const fetchPageAudio = async () => {
      if (!(section && recordedPage)) return;
      const audio = await Promise.all(
        transcripts.map(
          async ([sectionId]) => await UploadService.getPageAudio(recordedPage.storageFolderPath, sectionId),
        ),
      );
      setAudioDurations(transcripts.map(([_, transcript]) => transcript.duration));
      setPageAudio(audio);
    };
    fetchPageAudio();
  }, [section, recordedPage, transcripts]);

  useEffect(() => {
    const fetchPages = async () => {
      if (!bookId) return;
      setPages(await FirebaseService.getPages(bookId));
    };
    fetchPages();
  }, [bookId]);

  if (!bookId) return null;
  if (!studentId) return <Redirect to={`/Assignments/${assignmentId}/students/${students[0]}`} />;

  return (
    <div className="assignmentReadingCard">
      <Grid className="titleContainer" container wrap="nowrap" justifyContent="space-between">
        <Typography variant="subtitle1">Assigned Text</Typography>
        <ItemNav index={index} setIndex={setIndex} label="Page" length={recordedPages?.length || 0} />
      </Grid>
      <QuestionCard>
        {pageAudio && audioDurations ? (
          <ReadleeAudioPlayer audioUrls={pageAudio} audioDurations={audioDurations} narrow />
        ) : (
          <Typography variant="body1" align="center">
            No readings submitted yet
          </Typography>
        )}
        {currentPage && (
          <div className="readingContent">
            {currentPage.content.length ? (
              <p className="textContainer">{currentPage.content}</p>
            ) : (
              <Typography variant="body1" align="center">
                This is an independent reading assignment, text not available
              </Typography>
            )}
          </div>
        )}
      </QuestionCard>
    </div>
  );
};

export default AssignmentReading;
